import React, { useEffect, useRef, useState } from 'react';
import SavePopup from './SavePopup';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import CreateAccountPopup from 'components/Dashboard/components/CreateAccountPopup';
import { useSaveItemsPageFunctions } from 'components/SavedItems/useSaveItemsPageFunctions';
import { useSavedItemsContext } from 'components/SavedItems/SavedItemsContext';

const SaveItemPopup = () => {
  const { setState, state } = useSavedItemsContext();
  const {
    saveItemsIds,
    userSavedItemsFolders,
    reloadSavedItemsInformation,
    currentFolderSavedItems,
    showSavePopup,
    savePopupItem,
    savePopupMultipleItems,
  } = state;

  const [isLoading, setIsLoading] = useState(true);

  const { loadFolderItems, updateFolders, loadFoldersCall } = useSaveItemsPageFunctions();
  const [newFolderCreated, setNewFolderCreated] = useState(null);
  const [saveItemState, setSaveItemState] = useState('saveItem');
  const { generalApiCall } = useGeneralApiCall();
  const { isAuthenticated } = useAuth0();
  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';

  const initialCall = useRef(null);
  initialCall.current = async (source) => {
    try {
      if (isAuthenticated) {
        await callListFolders(source);
        await getSavedItemsIds(source);
        setIsLoading(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Saved items cancelled by the user');
    };
  }, []);

  const setShowPopup = () => {
    setState('showSavePopup', false);
    setTimeout(() => {
      setState('savePopupItem', null);
      setState('savePopupMultipleItems', null);
    }, 200);
  };

  const callListFolders = async (source) => {
    setState('readyUserSavedItemsFolders', false);
    let results = await loadFoldersCall(source);
    if (!!results) {
      setState('userSavedItemsFolders', results);
      setState('readyUserSavedItemsFolders', true);
      setState('isLoadingSavedItemsList', false);
    }
  };

  const getSavedItemsIds = async (source) => {
    const pathname = `/api/saved-items/saved-item-ids${showTweets ? '' : '?excludeTweets=true'}`;
    const method = 'get';
    const results = await generalApiCall({
      pathname,
      method,
      needsAuthentication: true,
      requestSource: source,
      notShowErrorMessage: true,
    });
    if (!!results) {
      setState('saveItemsIds', results);
    }
  };

  let objectId = (item) => {
    if (
      item.contributionId ||
      (item?.listItem &&
        (item.contentType === 'ScotlandCountrySpecificParliamentaryContribution' ||
          item.contentType === 'WalesCountrySpecificParliamentaryContribution'))
    ) {
      return item.parentObjectID;
    } else if (item.type === 'RankedStakeholder') {
      return item.baseObjectID;
    } else {
      return item.objectID ?? item.objectId; // Using objectId rather than objectID for consultations
    }
  };

  const addSaveItem = async (props) => {
    const { selectedFolderId, item, source, multipleItems } = props;
    const { type } = item;
    let pathname = `/api/saved-items/add-saved-item`;
    let contentType = () => {
      if (
        (item.contentType === 'ScotlandCountrySpecificParliamentaryContribution' ||
          item.contentType === 'WalesCountrySpecificParliamentaryContribution') &&
        item?.listItem
      ) {
        return 'CountrySpecificParliamentaryRecord';
      } else if (item.contentType === 'SeneddCommitteeTranscriptContribution') {
        return 'SeneddCommitteeTranscript';
      } else if (item.contributionId) {
        return 'HansardContent';
      } else if (item.type === 'RankedStakeholder') {
        return 'Stakeholder';
      } else if (type === 'WrittenQuestion') {
        return 'ParliamentaryRecord';
      } else if (item.objectType === 'UserContent') {
        return 'UserContent';
      } else if (type) {
        return type;
      } else {
        return item.objectType;
      }
    };
    let method = 'post';
    let requestProperties = {
      savedItemFolderId: selectedFolderId,
      contentType: contentType(),
      objectId: objectId(item),
    };
    let results = await generalApiCall({
      pathname,
      method,
      requestProperties,
      needsAuthentication: true,
      requestSource: source,
    });
    if (results) {
      if (!multipleItems) {
        createNotification(
          'success',
          (() => {
            return (
              <span>
                Saved to{' '}
                <Link to={`/saved-items?folder-id=${selectedFolderId}`}>
                  {userSavedItemsFolders.find((item) => item.id === selectedFolderId).name}
                </Link>
              </span>
            );
          })()
        );
      }
      return results;
    }
  };

  const addItemToMultipleFolders = async (props) => {
    const { e, selectedFolders, item, notLoadFolderItems, multipleItems } = props;
    let el = e.target;
    el.disabled = true;
    let itemOnFolders = createSelectedFolders(item);
    let itemSelected = currentItem(item);
    let foldersToAdd = selectedFolders.filter((folder) => !itemOnFolders.includes(folder));
    let foldersToRemove = itemOnFolders.filter((folder) => !selectedFolders.includes(folder));
    let foldersToRemoveMainObject = saveItemsIds[itemSelected]
      ? item?.savedItemsPerFolder.filter((folder) => foldersToRemove.includes(folder.folderId))
      : [];

    let itemsToAdd = [];
    let itemsToDelete = [];
    let newSavedItemsIds = [...saveItemsIds];
    for (let i = 0; i < foldersToAdd.length; i++) {
      let itemAdded = await addSaveItem({
        selectedFolderId: foldersToAdd[i],
        item,
        multipleItems,
      });
      if (itemAdded) {
        itemsToAdd.push(itemAdded);
      }
    }
    for (let i = 0; i < foldersToRemoveMainObject.length; i++) {
      let itemRemoved = await deleteItemFromFolder(foldersToRemoveMainObject[i]);
      if (itemRemoved) {
        itemsToDelete.push(itemRemoved);
      }
    }
    itemsToDelete.forEach((id) => {
      let itemPosition = newSavedItemsIds.findIndex((item) => item.id === id);
      newSavedItemsIds.splice(itemPosition, 1);
    });
    if (multipleItems) {
      return { itemsToAdd, itemsToDelete };
    } else {
      setState('saveItemsIds', [...newSavedItemsIds, ...itemsToAdd]);
      setState('reloadSavedItemsInformation', {
        updateNumber: reloadSavedItemsInformation?.number ? reloadSavedItemsInformation?.number + 1 : 1,
        itemId: objectId(item),
      });
      e.disabled = false;

      //UPDATE THE ITEMS ON THE SAVE ITEMS PAGE
      if (currentFolderSavedItems && foldersToRemove.includes(currentFolderSavedItems.id) && !notLoadFolderItems) {
        loadFolderItems(currentFolderSavedItems.id);
      }
    }
  };

  const createEditAFolder = async (props) => {
    const { e, name, source } = props;
    let el = e.target;
    el.disabled = true;
    let pathname = `/api/saved-items/create-saved-item-folder`;
    let method = 'post';
    let requestProperties = {
      name,
    };
    let results = await generalApiCall({
      pathname,
      method,
      needsAuthentication: true,
      requestProperties,
      requestSource: source,
    });
    if (results) {
      let newFolders = [...userSavedItemsFolders];
      newFolders = [...newFolders, results];
      setState('userSavedItemsFolders', newFolders);
      setSaveItemState('saveItem');
      setNewFolderCreated(results.id);
      createNotification('success', `Folder created successfully`);
    } else {
      el.disabled = false;
    }
  };

  const currentItem = (item) => {
    if (item !== null) {
      let defineObjectId = () => {
        if (item.contributionId) {
          return item.parentObjectID;
        } else if (item.type === 'RankedStakeholder') {
          return item.baseObjectID;
        } else {
          return item.objectID ?? item.objectId;
        }
      };
      let position = saveItemsIds.findIndex((element) => {
        let propertyToCompare = defineObjectId();
        return element.objectId === propertyToCompare;
      });
      if (position >= 0) {
        return position;
      }
    }
  };

  const deleteItemFromFolder = async (folder, source) => {
    const { id, folderName } = folder;
    let pathname = `/api/saved-items/saved-item/${id}`;
    let method = 'delete';
    let results = await generalApiCall({
      pathname,
      method,
      needsAuthentication: true,
      returnCompleteRequest: true,
      requestSource: source,
    });
    if (results && results.status === 200) {
      createNotification('success', `Removed from ${folderName} folder`);
      return id;
    }
  };

  const createSelectedFolders = (item) => {
    if (savePopupMultipleItems) {
      const itemsWithPosition = savePopupMultipleItems.filter(currentItem);
      if (itemsWithPosition.length === 0) return [];
      let commonFolderIds = {};
      for (let i = 1; i < itemsWithPosition.length; i++) {
        const folderIds = itemsWithPosition[i]?.savedItemsPerFolder?.map((f) => f.folderId) || [];
        commonFolderIds[itemsWithPosition[i]] = folderIds;
      }
      const intersection = Object.values(commonFolderIds).reduce(
        (acc, arr) => acc.filter((x) => arr.includes(x)),
        Object.values(commonFolderIds)[0] || []
      );
      return intersection;
    } else {
      let position = currentItem(item);
      const folders = item?.savedItemsPerFolder;
      if (position !== undefined && folders?.length > 0) {
        return folders.map((item) => item.folderId);
      } else {
        return [];
      }
    }
  };

  const conditionToShow = savePopupItem !== null || savePopupMultipleItems !== null;
  return (
    <>
      {!isLoading && conditionToShow && (
        <SavePopup
          setShowPopup={setShowPopup}
          showPopUp={showSavePopup && conditionToShow}
          userSavedItemsFolders={userSavedItemsFolders}
          item={savePopupItem}
          createEditAFolder={createEditAFolder}
          saveItemState={saveItemState}
          setSaveItemState={setSaveItemState}
          addItemToMultipleFolders={addItemToMultipleFolders}
          StakeholderItem={savePopupItem?.type === 'Stakeholder' || savePopupItem?.type === 'RankedStakeholder'}
          createSelectedFolders={createSelectedFolders}
          newFolderCreated={newFolderCreated}
          setNewFolderCreated={setNewFolderCreated}
          updateFolders={updateFolders}
          onAfterClose={() => {
            setSaveItemState('saveItem');
          }}
          multipleItems={savePopupMultipleItems}
        />
      )}
      {!isAuthenticated && (
        <CreateAccountPopup
          text={'You’ll need to create an account to save content'}
          setShowPopup={setShowPopup}
          showPopup={showSavePopup}
        />
      )}
    </>
  );
};

export default SaveItemPopup;
